import { type FormEvent, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ShieldCheck, ShieldAlert, ShieldX, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { identityServiceUrl } from "@/api/identityApi";

// OAuth Device Authorization Grant (RFC 8628) verification screen. A CLI tool
// running on a headless box (no local browser — e.g. Athavita.EiServer.Setup
// on a server-core machine) prints a short code and this page's URL. The
// human completes the *real* login (full MFA, no bypass) here, on whatever
// device they're reading the code from, then approves or denies the request.
// The actual OIDC mechanics (reading/validating the code, completing the
// device's token exchange) happen server-side against `/connect/verify` —
// this page only collects the code and renders what the server tells it.
export function DeviceVerifyPage() {
    const [params] = useSearchParams();
    const userCode = params.get("user_code");
    const error = params.get("error");
    const result = params.get("result");
    const client = params.get("client");
    const scope = params.get("scope");

    const [typedCode, setTypedCode] = useState("");

    const verifyUrl = new URL("/connect/verify", identityServiceUrl).toString();

    const handleSubmitCode = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const code = typedCode.trim();
        if (!code) return;
        window.location.assign(`${verifyUrl}?user_code=${encodeURIComponent(code)}`);
    };

    if (result === "approved") {
        return (
            <Card className="w-full">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <ShieldCheck className="h-5 w-5 text-green-600" />
                        Device approved
                    </CardTitle>
                    <CardDescription>
                        You can close this window and return to the installer — it will pick this up automatically.
                    </CardDescription>
                </CardHeader>
            </Card>
        );
    }

    if (result === "denied") {
        return (
            <Card className="w-full">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <ShieldX className="h-5 w-5 text-destructive" />
                        Request denied
                    </CardTitle>
                    <CardDescription>
                        You can close this window. The waiting installer will report that access was denied.
                    </CardDescription>
                </CardHeader>
            </Card>
        );
    }

    if (error === "invalid_code") {
        return (
            <Card className="w-full">
                <CardHeader>
                    <CardTitle>That code isn't valid</CardTitle>
                    <CardDescription>It may have expired, already been used, or been typed incorrectly.</CardDescription>
                </CardHeader>
                <CardContent>
                    <DeviceCodeForm typedCode={typedCode} setTypedCode={setTypedCode} onSubmit={handleSubmitCode} />
                </CardContent>
            </Card>
        );
    }

    if (userCode) {
        return (
            <Card className="w-full">
                <CardHeader>
                    <CardTitle>Approve this device?</CardTitle>
                    <CardDescription>
                        {client ? <><strong>{client}</strong> is requesting access to your account.</> : "An application is requesting access to your account."}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Alert className="mb-4">
                        <ShieldAlert className="h-4 w-4" />
                        <AlertTitle>Only approve this if you just started this install yourself</AlertTitle>
                        <AlertDescription>
                            Code: <span className="font-mono font-semibold">{userCode}</span>
                            {scope ? <div className="mt-1 text-xs text-muted-foreground">Requesting: {scope}</div> : null}
                        </AlertDescription>
                    </Alert>
                    <form method="post" action={verifyUrl} className="flex gap-3">
                        <input type="hidden" name="user_code" value={userCode} />
                        <Button type="submit" name="decision" value="accept" className="flex-1">
                            <ShieldCheck className="h-4 w-4" />
                            Approve
                        </Button>
                        <Button type="submit" name="decision" value="deny" variant="outline" className="flex-1">
                            <ShieldX className="h-4 w-4" />
                            Deny
                        </Button>
                    </form>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="w-full">
            <CardHeader>
                <CardTitle>Enter the code from your installer</CardTitle>
                <CardDescription>
                    Type the code shown in your terminal or console window.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <DeviceCodeForm typedCode={typedCode} setTypedCode={setTypedCode} onSubmit={handleSubmitCode} />
            </CardContent>
        </Card>
    );
}

function DeviceCodeForm({
    typedCode,
    setTypedCode,
    onSubmit
}: {
    typedCode: string;
    setTypedCode: (v: string) => void;
    onSubmit: (e: FormEvent<HTMLFormElement>) => void;
}) {
    return (
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div className="space-y-1.5">
                <Label htmlFor="device-code">Device code</Label>
                <Input
                    id="device-code"
                    autoComplete="off"
                    autoCapitalize="characters"
                    required
                    value={typedCode}
                    onChange={(e) => setTypedCode(e.target.value)}
                    placeholder="XXXX-XXXX"
                    autoFocus
                />
            </div>
            <Button type="submit" className="w-full" disabled={!typedCode.trim()}>
                <KeyRound className="h-4 w-4" />
                Continue
            </Button>
        </form>
    );
}
